import React, { useState } from 'react';
import { Box } from '@components/Box';
import { weddingWebsitePageData } from '@data-ts/pages/wedding-website';
import ReactHtmlParser from 'react-html-parser';
import { Columns } from '@components/Columns';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { GalleryECardModal } from '@components/GalleryComponents/GalleryECardModal/GalleryECardModal';
import { productTypes } from '@data-ts/components/GalleryComponents/GalleryPageContent';
import {
  StyledDesignColumns,
  StyledDesignColumn,
  StyledDesignTextColumn,
  StyledDesignTextImageColumn,
  StyledPlaceholderCloudImage,
  StyledTextPlaceholderCloudImage,
  CarouselSlideDetails,
  CtaWrapper,
  SlideDetailsBodyText,
  SlideDetailsText,
  StyledCTALink,
} from './DesignsSection.styles';
import { ButtonNoneStyled } from '@components/ButtonNoneStyled';
const analytics = require('../../../../static/js/joy');

interface DesignsSectionProps
  extends Readonly<{
    themes: MarketingData;
    sectionData: typeof weddingWebsitePageData.sections.designs;
  }> {}

export const DesignsSection: React.FC<DesignsSectionProps> = ({ sectionData, themes }) => {
  const sectionId = 'Design_Templates';
  const [isVisible, setIsVisible] = useState(false);
  const [isDisplayCardDetails, setCardDetailModalToggle] = useState(false);
  const { designs } = productTypes;
  const [cardDetails, setCardDetails] = useState<GalleryECard>({
    title: '',
    variationType: '',
    imageUrl: '',
    colors: [''],
    font: '',
    printUrl: '',
    tags: [''],
    vendor: '',
    productType: designs,
    variantColor: '',
    variantName: '',
    variantThemeId: '',
    theme: {
      name: '',
      themeId: '',
      marketingData: [],
    },
  });

  const handleCardClick = (themeId: string, layout: 'brannan' | 'aloha') => {
    const card: GalleryECard | undefined = themes.find(theme => theme.variantThemeId === themeId && theme.variationType === layout);
    if (typeof card !== 'undefined') {
      analytics.segment.sendTelemetryEvent('designBlockThemeClicked', {
        label: card.variantName ? card.variantName : card.theme?.name,
        value: 2,
        extraInfo: {
          version: 'abtestwwdesincurrent_2',
        },
      });
      setCardDetails(card);
      setCardDetailModalToggle(true);
    }
  };

  const handleCloseModal = () => {
    setCardDetailModalToggle(false);
  };

  return (
    <Box as={'section'} marginTop={[5, 0, 10]} marginBottom={[9, 0]} style={{ position: 'relative', zIndex: 2 }} id={sectionId}>
      <LazyLoadWrapper beforeLoad={() => setIsVisible(true)} threshold={0}>
        <div />
      </LazyLoadWrapper>
      <StyledDesignColumns stackOnMobile={false} centered verticalAlign="center" marginTop={[6, 10]} marginBottom={[6, 6]}>
        <StyledDesignColumn width={3} tablet={'four'} desktop={'four'} mobile="four">
          <ButtonNoneStyled
            id="DesignChampagneSparkle"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick('champagne_sparkle', 'aloha')}
          >
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/champagne-sparkle_aloha.png"
              alt="Champagne Sparkle Aloha"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
        <StyledDesignColumn width={3} tablet={'four'} desktop={'four'} mobile="four">
          <ButtonNoneStyled
            id="DesignFloralCrownGeorgia"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick('floral_crown_georgia', 'brannan')}
          >
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/floral-crown-georgia_brannan.png"
              alt="Floral Crown Georgia Brannan."
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
        <StyledDesignColumn width={3} tablet={'four'} desktop={'four'} mobile="four">
          <ButtonNoneStyled id="DesignMinimalistArch" skipRedirectWithTelemetry onClick={() => handleCardClick('minimalist_arch', 'aloha')}>
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/minimalist-arch_aloha.png"
              alt="Minimalist Arch Aloha"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
        <StyledDesignColumn width={3}>
          <ButtonNoneStyled
            id="DesignElephantCelebrationRed"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick('elephant_celebration_red', 'brannan')}
          >
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/elephant-celebration-red_brannan.png"
              alt="Elephant Celebration Red Brannan"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
      </StyledDesignColumns>
      <Columns centered verticalAlign="flex-start" marginTop={[6, 10]} marginBottom={[6, 6]}>
        <StyledDesignTextImageColumn width={4}>
          <ButtonNoneStyled id="DesignBeach" skipRedirectWithTelemetry onClick={() => handleCardClick('beach', 'brannan')}>
            <StyledTextPlaceholderCloudImage
              resizeWidth={355}
              isMainSrcShown={isVisible}
              src="wedding-website/design-gallery/beach_brannan.png"
              alt="Beach Brannan"
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignTextImageColumn>
        <StyledDesignTextColumn width={4}>
          <Box>
            <CarouselSlideDetails>
              <SlideDetailsText tagName={'h2'} variant={'hed56'} color={'neutra6'} marginBottom={6}>
                {ReactHtmlParser(sectionData.title)}
              </SlideDetailsText>
              <SlideDetailsBodyText tagName={'p'} variant={'dek20'} color={'neutra5'} marginBottom={[8, 6]}>
                {sectionData.content}
              </SlideDetailsBodyText>
              <CtaWrapper id="exploreDesignTemplates" forwardedAs={'a'} href="/designs">
                {sectionData.ctaText}
              </CtaWrapper>
              <StyledCTALink id="exploreDesignTemplatesLink" hasChevron={true} typography="featureHed17" href="/designs">
                {sectionData.ctaText}
              </StyledCTALink>
            </CarouselSlideDetails>
          </Box>
        </StyledDesignTextColumn>
        <StyledDesignTextImageColumn width={4}>
          <ButtonNoneStyled
            id="DesignAmethystTidepool"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick('amethyst_tidepool', 'brannan')}
          >
            <StyledTextPlaceholderCloudImage
              resizeWidth={355}
              src="wedding-website/design-gallery/amethyst-tidepool_brannan.png"
              alt="Amethyst Tidepool Brannan"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignTextImageColumn>
      </Columns>
      <StyledDesignColumns stackOnMobile={false} centered verticalAlign="flex-start" marginTop={[6, 10]} marginBottom={[6, 12]}>
        <StyledDesignColumn width={3}>
          <ButtonNoneStyled
            id="DesignTropicalBotanical"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick('tropical_botanical', 'brannan')}
          >
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/tropical-botanical_brannan.png"
              alt="Tropical Botanical Brannan"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
        <StyledDesignColumn width={3}>
          <ButtonNoneStyled id="DesignGrungeStone" skipRedirectWithTelemetry onClick={() => handleCardClick('grunge_stone', 'aloha')}>
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/grunge-stone_aloha.png"
              alt="Grunge Stone Aloha"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
        <StyledDesignColumn width={3}>
          <ButtonNoneStyled id="DesignPapelModerno" skipRedirectWithTelemetry onClick={() => handleCardClick('papel_moderno', 'aloha')}>
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/papel-moderno_aloha.png"
              alt="Papel Moderno Aloha"
              width="330"
              height="100%"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
        <StyledDesignColumn width={3}>
          <ButtonNoneStyled id="DesignGardenWindow" skipRedirectWithTelemetry onClick={() => handleCardClick('garden_window', 'brannan')}>
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/garden-window_brannan.png"
              alt="Garden Window Brannan"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
      </StyledDesignColumns>

      {isDisplayCardDetails && (
        <GalleryECardModal
          productType={designs}
          handleClose={handleCloseModal}
          cardDetails={cardDetails}
          isModalOpen={isDisplayCardDetails}
          isWeddingDesignSlider={true}
        />
      )}
    </Box>
  );
};
